/* eslint-disable import/no-cycle */
import { CareCoordinationReport, OralHealthCoachingReport } from '@brands/services/careCoordinationReport/types/types';

import { OptionCDT } from '../../../Utils/selectOptions';
import { Insurance, Organization, UserProfile } from '../../identity/types/UserProfile';
import { IOneReport } from '../../oneReport/types/IOneReport';
import { SmileScanReport } from './SmileScanReport';

export interface ConversationStats {
  conversation_sid: string;
  message_created_by: number;
  unread_message_count: number;
}

export interface VCStats {
  joined_at: string;
  left_at: string;
}

export interface Payment {
  id: string;
  amount: number;
  currency: string;
  note: null;
  status: string;
  completed_date: null;
  pricing: Pricing;
  payment_profile: PaymentProfile;
  created_at: Date;
}

export interface AdditionalInfo {
  additional_info: string;
  created_at: string;
}

export interface CaseClient {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
}

export interface PaymentProfile {
  id: number;
  type: string;
  is_default: boolean;
  is_active: boolean;
}

export interface Pricing {
  code: string;
  label: string;
  amount: number;
  currency: string;
  case_types: string[];
  is_active: boolean;
  payment_options?: string[];
  collect_insurance_info: boolean;
  amount_after_plan?: number;
  plan_decision_reason?: string;
  plan_group_id?: string;
  plan_free_remaining?: number;
  plan_percent_off?: number;

  created_at: Date;
}

export interface InsuranceMembership {
  insurance_carrier: InsuranceCarrier;
  insurance_carrier_id: number;
  group_number?: string;
  member_id: string;
}

export interface InsuranceCarrier {
  id: number;
  name: string;
}

export enum Status {
  Upcoming = 'upcoming',
  New = 'new',
  AcceptedCareAdvisor = 'accepted_care_advisor',
  AcceptedProvider = 'accepted_provider',
  CompletedByProvider = 'completed_by_provider',
  InProgressCareAdvisor = 'in_progress_care_advisor',
  InProgressProvider = 'in_progress_provider',
  Completed = 'completed',
  Deleted = 'deleted',
  PaymentErrored = 'payment_errored',
  PaymentReauthorizationFailed = 'payment_reauthorization_failed',
  Canceled = 'canceled',
  Default = 'default',
}

export enum CaseType {
  messaging = 'messaging',
  video_call_instant = 'video_call_instant',
  video_call_scheduled = 'video_call_scheduled',
  one_report = 'one_report',
  second_opinion = 'second_opinion',
  smile_scan = 'smile_scan',
  phone_call_scheduled = 'phone_call_scheduled',
  care_coordination = 'care_coordination',
  oral_health_coaching = 'oral_health_coaching',
  clinical_enablement = 'clinical_enablement',
  unknown = '',
}

export enum CareCoordinationStatus {
  follow_up_required = 'Requires follow-up',
  follow_up_in_progress = 'Follow-up in progress',
  follow_up_completed = 'Follow-up complete',
}

export interface CaseIntakeQuestions {
  is_dental_emergency?: boolean;
  in_pain?: boolean;
  pain_description?: string[];
  pain_time?: string;
  pain_level?: string;
  additional_info?: AdditionalInfo[];
  reason_for_visit?: string[];
}

export interface ICase {
  id: number;
  patient_id: number;
  /** Present when case is owned / assigned to a collector (used for FE permissions). */
  collector_id?: number;
  care_advisor_id: number;
  provider_id: number;
  type: CaseType;
  status: Status;
  video_conference_date: string | null;
  oral_health_report_date: string | null;
  patient_care_advisor_conversation_sid: string;
  patient_provider_conversation_sid: string;
  provider_care_advisor_conversation_sid: string;
  patient_collector_conversation_sid?: string | null;
  provider_collector_conversation_sid?: string | null;
  care_advisor_collector_conversation_sid?: string | null;
  patient_care_advisor_conversation_stats: ConversationStats[] | null;
  patient_provider_conversation_stats: ConversationStats[] | null;
  provider_care_advisor_conversation_stats: ConversationStats[] | null;
  created_at: string;
  started_at: string;
  updated_at: string | null;
  canceled_at: string | null;
  one_report: IOneReport;
  one_report_id: number;
  smile_scan_report: SmileScanReport | null;
  patient: UserProfile;
  organization: Organization;
  client?: CaseClient | null;
  client_division_id?: number | null;
  client_division?: { id: number; name: string } | null;
  provider: UserProfile;
  care_advisor: UserProfile;
  collector?: UserProfile | null;
  day_range?: string[] | null;
  time_range?: string[] | null;
  provider_assigned_by_patient?: boolean;
  video_conference_other_participants?: string | null;
  brandAssoc?: string;
  due_date?: string | null;
  completed_at: string;
  /** True once the case's patient report is finished. A staff status override completes a case without one. */
  report_available?: boolean;
  patient_vc_stats: VCStats | null;
  payment?: Payment;
  payment_profile: PaymentProfile;
  cdts?: OptionCDT[];
  insurance?: Insurance;
  insurance_id?: number;
  second_insurance?: Insurance;
  second_insurance_id?: number;
  /** The insurance (primary or secondary) that actually passed verification and was used for this case. */
  used_insurance?: Insurance;
  used_insurance_id?: number;
  paid_with_credits?: boolean;
  plan_discount_applied_percent?: number | null;
  patient_current_state?: string;
  pricing?: Pricing;
  client_tag?: string[];
  markers?: string[];
  patient_role?: string;
  payment_method?: string;
  patient_self_pay_cost?: string;
  insurance_carrier?: string;
  action?: any;
  caseType?: string;
  caseStatus?: string;
  care_coordination_report?: CareCoordinationReport;
  oral_health_coaching_report?: OralHealthCoachingReport;
  follow_up_status?: string;
  follow_up_addressed?: boolean;
  follow_up_type?: string;
  follow_up_urgency?: string;
  follow_up_description?: string;
  // Deferred follow-up selection saved during the consultation, materialized
  // into a linked case only when the report is submitted (case completed).
  pending_linked_case_type?: string | null;
  pending_linked_case_assign_to_me?: boolean | null;
  pending_follow_up_type?: string | null;
  pending_follow_up_urgency?: string | null;
  pending_follow_up_description?: string | null;
  case_intake_questions?: CaseIntakeQuestions;
  medical_history_snapshot?: any;
  medical_conditions_snapshot?: any;
  dental_history_snapshot?: any;
  // Multi-stage case orchestration: a case belongs to at most one journey,
  // ordered within it by journey_sequence_order. linked_cases is the journey's
  // other member cases when the API inlines them.
  case_journey_id?: number | null;
  journey_sequence_order?: number | null;
  case_journey?: ICaseJourney | null;
  linked_cases?: ICase[];
}

export interface ICaseJourney {
  id: number;
  patient_id: number;
  created_at: string;
  updated_at?: string | null;
  cases?: ICase[];
}

export interface RxPayload {
  api: {
    key: string;
    timestamp: string;
    signature: string;
  };
  location: {
    npi: string;
    practice_tax_id: string;
    name: string;
    practice_name: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    phone: string;
    fax: string;
  };
  user: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    npi: string;
    state_license_name: string;
    state_license_number: string;
    federal_drug_id: string;
  };
  patient: {
    id: number;
    first_name: string;
    last_name: string;
    dob: string;
    gender: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
  };
}
